<script setup lang="ts">
import { Ref, ref, watch } from 'vue';
import algoliasearch from 'algoliasearch/lite';
import {
  Combobox,
  ComboboxLabel,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from '@headlessui/vue'

import type { SearchAirlineCollection } from "@upclaim/sdk/src/data-contracts";
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import { CheckIcon } from '@heroicons/vue/24/solid';

const props = defineProps<{
  label: String;
}>();
const emits = defineEmits(['update:airline'])

const query = ref("");

function search(value: string, refine: any) {
  query.value = value;
  refine(query.value);
}
const client = algoliasearch(
    'X2I38XQM9R',
    '3dba72243a7c0848806f98cc5b2c5cbe'
);

const selected = ref<SearchAirlineCollection>([]);
watch(() => selected.value, (newValue) => {
  emits('update:airline', newValue)
})
</script>
<template>
  <ais-instant-search :search-client="client" index-name="airline">
    <ais-configure filters="isActive:true"></ais-configure>
    <ais-hits>
      <template v-slot="hits">
        <Combobox v-model="selected" v-slot="{ open, disabled, activeIndex, activeOption }" :nullable="true">
          <ComboboxLabel class="block text-sm font-medium text-left text-gray-700">
            <slot name="label"></slot>
          </ComboboxLabel>
          <div class="relative mt-1">
          <ais-search-box>
            <template v-slot="{ currentRefinement, isSearchStalled, refine }">
              <div class="relative flex items-center mt-1">
              <ComboboxButton class="w-full">
                <ComboboxInput type="search"
                  @change="search(($event.currentTarget as any)?.value.trim(), refine)"
                  :displayValue="(selected: any) => selected.name ? `${selected.name} (${selected.iata} / ${selected.icao})` : (null as any)"
                  :placeholder="selected || 'Rechercher...'"
                  autocomplete="on"
                  autocorrect="off"
                  autocapitalize="none"
                  spellcheck="false"
                  class="block w-full py-2 pl-3 pr-10 bg-white border border-gray-300 rounded-md shadow-sm text-semibold text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm"
                  :class="(selected as any)?.id && 'pl-8 border-2 items-center border-green-600'"
                />
              </ComboboxButton>

                <span v-if="isSearchStalled" class="absolute inset-y-0 right-12 flex py-2.5 pr-1.5">
                  Chargement...
                  <svg role="status" class="inline w-4 h-4 mx-2 text-blue-200 animate-spin"
                    viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor" />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="#1C64F2" />
                  </svg>
                </span>
                <div v-if="(selected as any)?.id" class="absolute inset-y-0 left-0 flex mx-2 my-2 bg-white">
                  <CheckIcon class="w-5 h-5 text-green-600" />
                </div>
                <div class="absolute inset-y-0 right-0 flex py-1.5 pr-1.5">
                  <kbd
                    class="inline-flex items-center px-2 font-sans text-sm font-medium text-gray-400 border border-gray-200 rounded">
                    <MagnifyingGlassIcon class="w-4 h-4" />
                  </kbd>
                </div>
              </div>
            </template>
          </ais-search-box>


            <TransitionRoot leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
              <ComboboxOptions
                class="absolute z-50 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                :key="hits.items.length">
                <div v-if="!hits.items.length"
                  class="relative px-4 py-2 text-gray-700 cursor-default select-none">
                  Pas de résultat.
                </div>

                <ComboboxOption v-for="item in hits.items" as="template" :key="item.id" :value="item" v-slot="{ selected, active }">
                  <li v-if="item.id" class="relative px-10 py-2 cursor-default select-none" :class="{
                    'bg-blue-600 text-white': active,
                    'text-gray-900': !active,
                  }">
                    <div class="flex items-center">
                      <span :class="['inline-block h-2 w-2 flex-shrink-0 rounded-full mr-4', item.isActive ? (item.isEC261 ? 'bg-blue-400' : 'bg-green-400') : 'bg-gray-200']" aria-hidden="true" />
                      <img :src="item.iconURL || 'https://ulysse.com/favicon.ico'" alt="" class="flex-shrink-0 w-6 h-6 rounded-full" />
                      <a :href="hits.sendEvent('click', item, 'click')" @click.prevent="hits.refine(item.value)"
                        class="mx-4 truncate"
                      >
                        <ais-highlight attribute="name" :hit="item" />
                        <span :class="['ml-2 font-semibold truncate', active ? 'text-white' : 'text-gray-500']">
                          <ais-highlight attribute="iata" :hit="item" />
                        </span>
                      </a>
                      <span v-if="selected" class="absolute inset-y-0 left-0 flex items-center pl-3"
                        :class="{ 'text-white': active, 'text-blue-600': !active }">
                        <CheckIcon class="w-5 h-5" aria-hidden="true" />
                      </span>
                    </div>
                  </li>
                </ComboboxOption>
              </ComboboxOptions>
            </TransitionRoot>
          </div>
        </Combobox>
      </template>
    </ais-hits>
  </ais-instant-search>
</template>
